import { ReactNode, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Label,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Select,
  Spinner,
  Textarea,
  makeStyles,
} from '@fluentui/react-components';
import { useMutation } from '@tanstack/react-query';
import { ArrowRight, Copy, ExternalLink, X } from 'lucide-react';

import {
  ActivateWaveResult,
  AssertionIdentity,
  MintSession,
  activateWave,
  waveErrorCode,
  MintReadiness,
} from '../api/waves';
import { LicenseBalance } from '../api/licensing';
import { BrowserSignInPhase, useBrowserSignInActivation } from '../hooks/useBrowserSignInActivation';
import { useDeviceCodeActivation } from '../hooks/useDeviceCodeActivation';
import { SignInHelperState, useSignInHelper } from '../hooks/useSignInHelper';
import { DeviceCodeBlockState, deviceCodeBlockState, deviceCodeLikelyBlocked } from '../lib/deviceCodeBlocking';
import { ActivationChecks, SignInMethod, deriveActivationChecks } from '../lib/activationReadiness';
import { errorText } from '../lib/errorText';
import { migrationTypeLabel } from '../lib/vocabulary';
import { IDENTIFIER, NUMERIC, RADIUS, STATUS, STROKE, SURFACE, TEXT } from '../theme/tokens';

import ActivationStepper, { activationStage } from './ActivationStepper';
import AdvancedSignInOptions from './AdvancedSignInOptions';
import BrandLogo from './BrandLogo';
import EnvironmentEcho from './EnvironmentEcho';
import ActivationReadinessSummary from './ActivationReadinessSummary';
import ActivationTechnicalDetails from './ActivationTechnicalDetails';
import { useLicenseBalance } from './LicenseBalancePanel';
import { useMintReadiness } from './MintReadinessPanel';
import SignInHelperCard from './SignInHelperCard';
import { DeviceCodeBlockNotice, ReloadedSignInNotice } from './SignInPathNotices';
// FD-305 (FD-ADR-032) — wave activation, staged.
//
// Design direction: PRECISION INSTRUMENT. One dialog, one live region, no
// decoration. The whole surface exists to get a human through one interactive
// sign-in without losing track of which account, which tenant, or how long they
// have — so the code, the account and the countdown are the only things given
// visual weight, and everything else is a line of text.
//
// WHAT THE CONSOLE STILL CANNOT DO IS MINT. That has not changed and the copy
// must not imply otherwise: the mint needs an interactive sign-in as the
// destination tenant's BPRT minting service account, which is a person at a
// keyboard. What FD-ADR-032 changed is where the resulting DRS access token
// lives — the Manager redeems the grant server-side, so the token never enters
// this browser. The console holds only a `deviceCode`, which is inert until the
// paired sign-in completes.
//
// Extracted from WavePanel rather than grown inside it: that file is already
// 634 lines, this adds ~250, and the hook test and the dialog test want
// different harnesses.
//
// FD-592 (FD-ADR-047) — THREE sign-in paths, in this order, and the order is the
// decision. (1) The BROWSER sign-in leads, because most customer tenants block
// device code by Conditional Access: it signs in through the same Microsoft
// first-party client in a new tab, and the AirBridge sign-in helper extension
// carries the code back — or the operator pastes the localhost address the tab
// lands on. (2) DEVICE CODE is second, unchanged, with the named probe's warning
// when a policy blocks it. (3) The PASTED TOKEN is the last resort, unchanged.
// Every path ends in the same server-side redeem, claim check, checkout and mint,
// and none of them puts the DRS access token in this browser (path C aside, which
// was always the explicit exception).

// FD-611 — the simplified layout (design board 10). Same dialog, same logic, same
// calls; the surface is re-arranged so the decision an operator actually makes
// leads: a header naming the campaign and destination, the three-stage stepper, ONE
// counted readiness card, the three sign-in paths as cards, the credential lifetime,
// and every diagnostic moved — not dropped — into "Advanced details", collapsed by
// default. Nothing on this surface gates the primary button on readiness: the
// server refuses on its own terms (FD-296).

// FD-612 — the second pass (design board 11). The default view is the Microsoft
// sign-in alone: one line of explanation and the AirBridge Sign-in Helper card. The
// method cards are gone; device code and a pasted token live in a collapsed
// "Advanced sign-in options" radio list, each revealing its own controls inline.
// Path-specific explanations appear only where they apply: the paste-the-address
// instructions only once the browser sign-in has started. Diagnostics are
// "Technical details", collapsed. Same logic, same calls, same payloads.

/** Which sign-in path the operator chose. The radio order in AdvancedSignInOptions is the page order (asserted). */
export type { SignInMethod };

/**
 * FD-611 — the lifetimes offered in the dropdown. The server's own default is
 * merged in when it is not one of these, so the pre-selected value is always the
 * one the server applies. "Custom" keeps the per-wave override to ANY whole number
 * in FD-ADR-021's 1–90 ceiling, exactly as the free-form field allowed.
 */
const LIFETIME_PRESETS = [1, 3, 7, 14, 30, 60, 90] as const;

const useStyles = makeStyles({
  // Board 11: ~600px. The body scrolls inside a 768px-tall viewport while
  // DialogActions stays pinned (DialogBody's grid does that).
  surface: { maxWidth: '600px', width: '100%' },
});

/**
 * Why the primary button is briefly disabled (FD-501).
 *
 * Stated as a `title` rather than as a fourth line of body copy: the panel above
 * is already saying it with a spinner and a progress bar, and the operator only
 * needs this if they reach for the button and find it inert.
 */
const CHECKING_HINT =
  'Reading the migration licence balance. Sign-in enables as soon as it answers, ' +
  'whether or not licences are available.';

/** Both paths need this; the guided one gets it from the server, so it is stated once. */
const PASTE_COMMAND = 'az account get-access-token --resource urn:ms-drs:enterpriseregistration.windows.net';

/**
 * FD-442 (FD-ADR-042 §7) — used ONLY when the Manager is older than FD-442 and sends
 * no `waveDefaults`.
 *
 * This is NOT a second source of truth, and the difference matters. Before FD-442 the
 * number lived here AND in `manager/activate-wave` as two independent literals, either
 * of which could have moved without the other being wrong on its own. Now the server
 * owns it; this constant only says what a pre-FD-442 Manager will do, which is exactly
 * what it has always done — so a console ahead of its backend is byte-identical rather
 * than confidently wrong.
 */
const LEGACY_EXPIRY_DAYS = 30;

export interface ActivateWaveDialogProps {
  open: boolean;
  migrationId: string;
  /**
   * FD-435 (FD-ADR-041) — how many migration licenses this activation reserves.
   *
   * **This comes from the SERVER and is never computed here.** It is the
   * migration row's own `deviceCount`, which is the same column FD-430's
   * checkout falls back to when it counts the `DeviceMigrations` partition. A
   * client-side count of whatever devices a grid happened to have loaded would
   * be a different number from the one the server reserves, stated with the
   * same confidence — and the operator would have no way to tell which.
   *
   * Optional so a caller that cannot supply it renders honest copy with no
   * number rather than a fabricated one.
   */
  deviceCount?: number | null;
  /**
   * FD-442 (FD-ADR-042 §7) — the lifetime `activate-wave` applies when a request
   * omits `expiryDays`, resolved from this deployment's `BPRT_DEFAULT_EXPIRY_DAYS`
   * and served by `get-migration`.
   *
   * **This comes from the SERVER and is never a constant here.** The operator is
   * about to mint a tenant-join credential that Rubix cannot revoke (FD-ADR-034
   * row 16), so the lifetime shown must be the lifetime applied. Optional: a Manager
   * older than FD-442 sends nothing and `LEGACY_EXPIRY_DAYS` covers that case.
   */
  defaultExpiryDays?: number;
  /** FD-611 — header context. All optional, and each is left out rather than guessed. */
  migrationName?: string | null;
  /** The WIRE value (`D2C` / `T2T`); rendered as Hybrid / Cloud. */
  migrationType?: string | null;
  /** The destination domain label; falls back to the server-resolved readiness domain. */
  destinationDomain?: string | null;
  onClose: () => void;
  /** Invalidate the migration — the wave's state changed. */
  onDone: () => void;
}

export default function ActivateWaveDialog({
  open,
  migrationId,
  deviceCount,
  defaultExpiryDays,
  migrationName,
  migrationType,
  destinationDomain,
  onClose,
  onDone,
}: ActivateWaveDialogProps) {
  const styles = useStyles();
  const [method, setMethod] = useState<SignInMethod>('browser');
  // FD-611 — true once the operator chose "Custom…" in the lifetime dropdown.
  const [customLifetime, setCustomLifetime] = useState(false);

  // The server's default, sanitised to the range activate-wave will accept. A value
  // outside 1–90 could only come from a Manager that would refuse it anyway, so it is
  // treated as absent rather than pre-filling a field that cannot be submitted.
  const serverDefault =
    typeof defaultExpiryDays === 'number' &&
    Number.isInteger(defaultExpiryDays) &&
    defaultExpiryDays >= 1 &&
    defaultExpiryDays <= 90
      ? defaultExpiryDays
      : LEGACY_EXPIRY_DAYS;

  // NULL MEANS "THE OPERATOR HAS NOT TOUCHED THIS", which is why the state is not
  // seeded with the default via useState's initialiser: that runs once, and this
  // dialog is MOUNTED CLOSED by WavePanel (a Fluent Dialog mounted already-open never
  // enters the a11y tree — FLUENT_UI_ADDENDUM), so it can easily mount before the
  // migration query resolves. Seeding would then freeze a stale fallback into a field
  // the operator reads as authoritative. Deriving instead means an untouched field
  // always shows what the server will apply, and a touched one is never overwritten.
  const [typedExpiryDays, setExpiryDays] = useState<string | null>(null);
  const expiryDays = typedExpiryDays ?? String(serverDefault);
  const [token, setToken] = useState('');
  const [announcement, setAnnouncement] = useState('');
  // Set when the SERVER says the guided path is off for this deployment. There
  // is deliberately no VITE_ flag mirroring DRS_DEVICE_CODE_ENABLED: a second
  // copy of a Manager app setting is a second thing to keep in step, and the
  // one that drifts is always the one nobody redeploys.
  const [guidedDisabled, setGuidedDisabled] = useState(false);

  const activation = useDeviceCodeActivation(migrationId, onDone);
  const { phase, start, cancel } = activation;
  // FD-592 — path B. Its PKCE verifier lives inside this hook's memory only.
  const browser = useBrowserSignInActivation(migrationId, onDone);
  const browserPhase = browser.phase;
  // FD-592 — is the sign-in helper here? Advisory copy only; it gates nothing.
  const helper = useSignInHelper(open);

  // FD-435 — ON DIALOG OPEN, NEVER ON THE POLL.
  //
  // The hook lives HERE rather than inside `SetupStage` on purpose. It stays
  // subscribed for as long as the dialog is open, so the read happens exactly
  // once per open: a stage change (setup → sign-in → activating) is a re-render
  // of this component, not a remount, and `useDeviceCodeActivation`'s ~5s
  // settle-then-schedule chain is wired to nothing in this query. ATC Licensing
  // is HA-off with 5–30s cold starts, so a balance read on that loop would be
  // ~180 cold calls per activation (FD-ADR-032 §4, applied to a read).
  //
  // Gated on `open` because this component is mounted unconditionally by
  // `WavePanel` — see `useLicenseBalance`. Asserted by a timer test in this
  // component's suite, not by this comment.
  const balance = useLicenseBalance(open);
  // FD-451 — the minting account's prerequisites, read when the dialog OPENS so
  // a missing role surfaces here rather than as AADSTS240005 partway through an
  // interactive sign-in. Same fetch policy as the balance above and for the same
  // reason: never on the ~5 s activation poll. It never gates activation.
  const mintReadiness = useMintReadiness(migrationId, open);

  // FD-501 — SIGN-IN WAITS FOR THE BALANCE READ, AND ONLY FOR THE READ.
  //
  // The owner's ask was that the licence check gate the sign-in rather than sit
  // beside it. What that means precisely matters, because the obvious reading is
  // the one thing this console must not do.
  //
  // This gates on the check having ANSWERED. It does NOT gate on the answer.
  // `isLoading` is false the moment the query settles — success or failure — so
  // a shortfall, an over-drawn account, `LICENSING_URL` unset, a `404
  // account_not_mapped` and a cold ATC that times out all release the button
  // immediately. FD-ADR-024's advisory posture is the reason: a licensing outage
  // must never wedge a migration, and pool capacity — the one bucket that really
  // blocks — is refused SERVER-SIDE by `activate-wave` with
  // `409 LICENSE_POOL_EXHAUSTED`. A console that blocked on a balance it read
  // would be the FD-296 class of gate: one that only runs where someone happens
  // to be standing, while the real control lives elsewhere and disagrees.
  //
  // So the value of the gate is ordering, not enforcement: an operator does not
  // start an interactive sign-in they cannot un-start before the console has
  // finished telling them what it costs.
  const balanceChecking = balance.isLoading;

  // The count the server will reserve, sanitised to a POSITIVE INTEGER or null.
  // Null means "say no number" — never "say zero", and never invent one.
  const reserveCount =
    typeof deviceCount === 'number' && Number.isInteger(deviceCount) && deviceCount > 0
      ? deviceCount
      : null;

  const days = Number(expiryDays);
  const daysValid = Number.isInteger(days) && days >= 1 && days <= 90;

  const paste = useMutation({
    mutationFn: () =>
      activateWave(migrationId, { drsAccessToken: token.trim(), expiryDays: days }),
    onSuccess: (result) => {
      onDone();
      // FD-341b: hold the dialog open ONLY when the server reported a verdict.
      // Absent `assertionIdentity` — an older Manager, or the reconcile path —
      // closes exactly as it did before, so a console ahead of its backend is
      // byte-identical to today rather than showing an empty row.
      if (!result.assertionIdentity) close();
    },
  });

  // FD-341b — the one thing worth holding the dialog open for. Both paths land
  // here: the guided phase carries its result, the paste mutation caches its
  // own. Whichever produced it, the operator sees the verdict as part of the
  // activation rather than by decoding a JWT afterwards.
  const activated: ActivateWaveResult | null =
    phase.kind === 'done'
      ? phase.result
      : browserPhase.kind === 'done'
        ? browserPhase.result
        : (paste.data ?? null);
  const verdict: AssertionIdentity | null = activated?.assertionIdentity ?? null;

  // FD-493 — which of the three real stages this activation is on. Derived by a
  // pure function in `ActivationStepper` rather than inline, so it is asserted
  // against every `ActivationPhase` kind instead of inferred from whatever this
  // component happened to render.
  //
  // `activated` rather than `verdict`: an older Manager returns no
  // `assertionIdentity`, and that activation is still COMPLETE. Keying the last
  // stage on the verdict would leave a successful mint showing "Mint credential"
  // for the instant before the dialog closes itself.
  // The browser path's one in-flight `activate-wave` IS the mint, exactly as a
  // pasted token's is — so it shares the paste path's stage-2 signal.
  const browserMinting = browserPhase.kind === 'activating';
  const stage = activationStage(phase, {
    pasteBusy: paste.isPending || browserMinting,
    activated: activated !== null,
  });

  const blockState = deviceCodeBlockState(mintReadiness.data);

  // FD-611 — ONE derivation feeds the summary's counts and the Advanced details
  // checklist, so the two cannot disagree. Counted, never typed in.
  const checks = deriveActivationChecks({
    readiness: mintReadiness,
    balance,
    reserveCount,
    method,
  });

  const destination = destinationDomain || mintReadiness.data?.tenantDomain || null;
  const headerParts = [
    migrationName || null,
    migrationType ? migrationTypeLabel(migrationType) : null,
    destination,
  ].filter((p): p is string => Boolean(p));
  const busyMinting = paste.isPending || browserMinting;

  function close() {
    // The pasted token leaves component state on every close path — success,
    // cancel, or dismiss. Cancelling the guided path makes NO server call:
    // a device code has no revoke, it simply expires.
    setToken('');
    setAnnouncement('');
    paste.reset();
    cancel();
    browser.cancel();
    onClose();
  }

  // A successful guided mint closes the dialog. `useDeviceCodeActivation` has
  // already invalidated the migration and called onDone by this point.
  useEffect(() => {
    if (phase.kind === 'done' && !phase.result.assertionIdentity) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.kind]);

  // The same for the browser path.
  useEffect(() => {
    if (browserPhase.kind === 'done' && !browserPhase.result.assertionIdentity) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browserPhase.kind]);

  // The deployment turned the guided path off (DRS_DEVICE_CODE_ENABLED=false,
  // typically because Conditional Access blocks device-code in that tenant).
  // Learn it from the authority rather than from a mirrored flag, and switch
  // to paste rather than leaving a button that can only fail.
  useEffect(() => {
    if (phase.kind === 'failed' && waveErrorCode(phase.error) === 'DEVICE_CODE_DISABLED') {
      setGuidedDisabled(true);
      // FD-592: DRS_DEVICE_CODE_ENABLED governs device code ONLY (FD-ADR-047 §6),
      // so the browser sign-in — the path such a deployment needs — stays open.
      setMethod('browser');
      cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && close()}>
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close dialog"
                icon={<X size={18} strokeWidth={1.5} />}
                onClick={close}
                // Mid-mint the one in-flight call must be allowed to land; Cancel
                // is disabled for the same reason.
                disabled={busyMinting}
              />
            }
          >
            Activate wave
          </DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-4">
              {/* FD-611 — which campaign, which kind, which destination. Each part is
                  left out rather than guessed when the caller did not supply it. */}
              {headerParts.length > 0 && (
                <p
                  className="-mt-2 m-0 flex flex-wrap items-center gap-x-2 text-[13px]"
                  style={{ color: TEXT.secondary }}
                  data-testid="activate-header-context"
                >
                  {headerParts.map((part, i) => (
                    <span key={`${i}-${part}`} className="inline-flex items-center gap-2">
                      {i > 0 && <span aria-hidden="true">·</span>}
                      <span style={i === 0 ? { color: TEXT.primary, fontWeight: 500 } : undefined}>{part}</span>
                    </span>
                  ))}
                </p>
              )}

              {/* FD-422 — which deployment this activation mints a tenant-join
                  credential in. Up top, because it is the fact that decides
                  whether the rest of the dialog should be read at all. */}
              <EnvironmentEcho />

              {/* One polite region for phase changes and the copy confirmation.
                  The countdown is deliberately NOT in here — it would announce
                  every second and drown everything else out. */}
              <div aria-live="polite" className="sr-only">
                {announcement}
              </div>

              {/* FD-493 (mockups 5-7) — the spine. Rendered on EVERY stage
                  including setup, because its job is to tell an operator how
                  many steps a wave activation has before they start one, not
                  only where they are once they have. */}
              <ActivationStepper stage={stage} />

              {verdict ? (
                <ActivatedStage verdict={verdict} />
              ) : browserPhase.kind === 'starting' ? (
                <div className="flex items-center gap-3 py-2">
                  <Spinner size="tiny" />
                  <span className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
                    Opening the Microsoft sign-in…
                  </span>
                </div>
              ) : browserPhase.kind === 'awaiting-return' || browserPhase.kind === 'activating' ? (
                <BrowserSignInStage
                  phase={browserPhase}
                  helperDetected={helper.everDetected}
                  onSubmit={browser.submitReturnUrl}
                />
              ) : browserPhase.kind === 'failed' ? (
                <ActivateError error={browserPhase.error} />
              ) : phase.kind === 'awaiting-signin' || phase.kind === 'activating' ? (
                <SignInStage phase={phase} onCopy={copyCode} onCancel={close} />
              ) : phase.kind === 'starting' ? (
                <div className="flex items-center gap-3 py-2">
                  <Spinner size="tiny" />
                  <span className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
                    Starting the sign-in…
                  </span>
                </div>
              ) : phase.kind === 'expired' ? (
                <MessageBar intent="warning">
                  <MessageBarBody>
                    <MessageBarTitle>The sign-in window closed</MessageBarTitle>
                    Nothing was minted and there is nothing to clean up — the code simply expired.
                    Start over to get a new one.
                  </MessageBarBody>
                </MessageBar>
              ) : phase.kind === 'failed' ? (
                <ActivateError error={phase.error} />
              ) : (
                <SetupStage
                  method={method}
                  onMethod={setMethod}
                  guidedDisabled={guidedDisabled}
                  expiryDays={expiryDays}
                  onExpiryDays={setExpiryDays}
                  serverDefault={serverDefault}
                  customLifetime={customLifetime}
                  onCustomLifetime={setCustomLifetime}
                  daysValid={daysValid}
                  token={token}
                  onToken={setToken}
                  busy={paste.isPending}
                  pasteError={paste.isError ? paste.error : null}
                  reserveCount={reserveCount}
                  checks={checks}
                  balance={balance.data}
                  balanceLoading={balance.isLoading}
                  balanceError={balance.error}
                  mintReadiness={mintReadiness.data}
                  mintReadinessLoading={mintReadiness.isLoading}
                  mintReadinessError={mintReadiness.error}
                  blockState={blockState}
                  helper={helper}
                />
              )}
            </div>
          </DialogContent>
          <DialogActions fluid>
            {verdict ? (
              // The wave IS active by now. "Cancel" beside a completed
              // activation reads as "undo", which nothing here can do.
              <>
                <span aria-hidden="true" style={{ flex: 1 }} />
                <Button appearance="primary" onClick={close}>
                  Close
                </Button>
              </>
            ) : (
              <>
            <Button appearance="secondary" onClick={close} disabled={busyMinting}>
              Cancel
            </Button>
            {/* Cancel left, the action right (board 10). */}
            <span aria-hidden="true" style={{ flex: 1 }} />
            {browserPhase.kind === 'failed' ? (
              // A fresh session, state and verifier. Never a replay of the spent
              // code — Entra codes redeem once (FD-ADR-047 §3).
              <Button appearance="primary" onClick={() => browser.start(days)} disabled={!daysValid}>
                Start over
              </Button>
            ) : browserPhase.kind !== 'idle' ? null : phase.kind === 'expired' || phase.kind === 'failed' ? (
              // Start over PRESERVES expiryDays — re-choosing it is pure friction
              // after a failure the operator did not cause.
              <Button appearance="primary" onClick={() => start(days)} disabled={!daysValid}>
                Start over
              </Button>
            ) : method === 'paste' ? (
              <Button
                appearance="primary"
                disabled={
                  token.trim().length === 0 || !daysValid || paste.isPending || balanceChecking
                }
                title={balanceChecking ? CHECKING_HINT : undefined}
                onClick={() => paste.mutate()}
              >
                {paste.isPending ? 'Activating…' : balanceChecking ? 'Checking…' : 'Activate wave'}
              </Button>
            ) : method === 'browser' && phase.kind === 'idle' ? (
              <Button
                appearance="primary"
                disabled={!daysValid || balanceChecking}
                title={balanceChecking ? CHECKING_HINT : 'Opens the Microsoft sign-in in a new tab'}
                icon={<ArrowRight size={16} strokeWidth={1.5} />}
                iconPosition="after"
                onClick={() => browser.start(days)}
              >
                {balanceChecking ? (
                  'Checking licences…'
                ) : (
                  // The Microsoft mark is decorative here: the words name the provider.
                  <span className="inline-flex items-center gap-2">
                    <BrandLogo brand="microsoft" size={16} />
                    Sign in with Microsoft
                  </span>
                )}
              </Button>
            ) : phase.kind === 'idle' ? (
              <Button
                appearance="primary"
                disabled={!daysValid || balanceChecking}
                title={balanceChecking ? CHECKING_HINT : undefined}
                onClick={() => start(days)}
              >
                {balanceChecking ? 'Checking licences…' : 'Get device code'}
              </Button>
            ) : null}
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );

  async function copyCode(code: string) {
    // Undefined in jsdom and in any non-secure context. The code is selectable
    // text regardless, so this degrades to "do it yourself" rather than throwing.
    if (!navigator.clipboard?.writeText) {
      setAnnouncement('Copy is not available here — select the code and copy it manually.');
      return;
    }
    try {
      await navigator.clipboard.writeText(code);
      setAnnouncement('Code copied.');
    } catch {
      setAnnouncement('Could not copy — select the code and copy it manually.');
    }
  }
}

// ——— stage 1: choose a path ————————————————————————————————————————————

function SetupStage({
  method,
  onMethod,
  guidedDisabled,
  expiryDays,
  onExpiryDays,
  serverDefault,
  customLifetime,
  onCustomLifetime,
  daysValid,
  token,
  onToken,
  busy,
  pasteError,
  reserveCount,
  checks,
  balance,
  balanceLoading,
  balanceError,
  mintReadiness,
  mintReadinessLoading,
  mintReadinessError,
  blockState,
  helper,
}: {
  method: SignInMethod;
  onMethod: (m: SignInMethod) => void;
  guidedDisabled: boolean;
  expiryDays: string;
  onExpiryDays: (v: string) => void;
  serverDefault: number;
  customLifetime: boolean;
  onCustomLifetime: (v: boolean) => void;
  daysValid: boolean;
  token: string;
  onToken: (v: string) => void;
  busy: boolean;
  pasteError: unknown;
  reserveCount: number | null;
  checks: ActivationChecks;
  balance: LicenseBalance | undefined;
  balanceLoading: boolean;
  balanceError: unknown;
  mintReadiness: MintReadiness | undefined;
  mintReadinessLoading: boolean;
  mintReadinessError: unknown;
  blockState: DeviceCodeBlockState;
  helper: SignInHelperState;
}) {
  const deviceCodeBlocked = deviceCodeLikelyBlocked(blockState);

  // Each path's own controls, revealed inline under its radio. The handlers and the
  // validation are the dialog's, unchanged; only where they render moved.
  const deviceCodeControls = (
    <div className="flex flex-col gap-1.5">
      <p className="m-0 text-[12.5px] leading-snug" style={{ color: TEXT.secondary }}>
        Get a code, then enter it at microsoft.com/devicelogin while signed in as the minting
        account. The console continues as soon as you finish.
      </p>
      {/* The named probe, visible here because device code is the chosen path
          (FD-ADR-047 §2). Elsewhere it is only the option's badge and a note. */}
      <DeviceCodeBlockNotice state={blockState} prominent />
    </div>
  );

  const pasteControls = (
    <div className="flex flex-col gap-1">
      <Label htmlFor="drs-token" required>
        DRS access token
      </Label>
      <Textarea
        id="drs-token"
        value={token}
        onChange={(_, d) => onToken(d.value)}
        placeholder="Paste the token obtained from the sign-in"
        resize="vertical"
        rows={3}
        disabled={busy}
      />
      <span className="font-display text-xs" style={{ color: TEXT.secondary }}>
        Sign in as the minting service account, then run:
      </span>
      <code
        className="text-xs"
        style={{
          ...IDENTIFIER,
          color: TEXT.primary,
          backgroundColor: SURFACE.rowFill,
          padding: '6px 8px',
          borderRadius: RADIUS.sm,
          overflowWrap: 'anywhere',
        }}
      >
        {PASTE_COMMAND}
      </code>
      <span className="font-display text-xs" style={{ color: TEXT.secondary }}>
        Sent once and never stored. It is not saved, logged, or placed in a URL.
      </span>
    </div>
  );

  return (
    <>
      {/* FD-611 — one counted card instead of three stacked panels. */}
      <ActivationReadinessSummary checks={checks} />

      {helper.orphanResult && <ReloadedSignInNotice />}

      {guidedDisabled && (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>Device-code sign-in is off for this deployment</MessageBarTitle>
            The Manager has <code>DRS_DEVICE_CODE_ENABLED=false</code>, usually because
            Conditional Access blocks the device-code flow in that tenant. Sign in with
            Microsoft in a new tab instead, or paste a token you obtained yourself.
          </MessageBarBody>
        </MessageBar>
      )}

      <section className="flex flex-col gap-2">
        <div className="flex flex-col gap-0.5">
          <h3 className="m-0 text-[15px] font-semibold" style={{ color: TEXT.primary }}>
            Sign in with Microsoft
          </h3>
          {/* What the console still cannot do is mint on its own (FD-ADR-032). */}
          <p className="m-0 text-[12.5px] leading-snug" style={{ color: TEXT.secondary }}>
            A person has to sign in as the destination tenant’s minting service account. AirBridge
            brokers the sign-in; the token never reaches this browser.
          </p>
        </div>
        {/* Advisory. It never gates the primary button (FD-296). */}
        <SignInHelperCard helper={helper} />
      </section>

      <section className="flex flex-col gap-1.5">
        <h3 className="m-0 text-[15px] font-semibold" style={{ color: TEXT.primary }}>
          Credential settings
        </h3>
        {/* FD-435 (FD-ADR-041) — the sentence that makes the per-device licence unit
            legible at the moment it is consumed: a 500-device wave reserves 500. */}
        <p className="m-0 text-[12.5px] leading-snug" style={{ color: TEXT.secondary }}>
          Activating mints the wave’s join credential and{' '}
          {reserveCount === null ? (
            <>reserves one migration license per assigned device</>
          ) : (
            <>
              reserves <span style={NUMERIC}>{reserveCount}</span>{' '}
              {reserveCount === 1 ? 'migration license' : 'migration licenses'} (one per device)
            </>
          )}
          . Devices cannot begin until this happens.
        </p>
        <CredentialLifetimeField
          expiryDays={expiryDays}
          onExpiryDays={onExpiryDays}
          serverDefault={serverDefault}
          custom={customLifetime}
          onCustom={onCustomLifetime}
          daysValid={daysValid}
          disabled={busy}
        />
      </section>

      {/* FD-ADR-047 §1 — the ORDER is the decision: Microsoft first, device code
          second, a pasted token last. A test asserts it. */}
      <AdvancedSignInOptions
        method={method}
        onMethod={onMethod}
        guidedDisabled={guidedDisabled}
        blockState={blockState}
        deviceCodeBlocked={deviceCodeBlocked}
        controls={{ 'device-code': deviceCodeControls, paste: pasteControls }}
      />

      <ActivationTechnicalDetails
        checks={checks}
        readiness={mintReadiness}
        readinessLoading={mintReadinessLoading}
        readinessError={mintReadinessError}
        balance={balance}
        balanceLoading={balanceLoading}
        balanceError={balanceError}
        reserveCount={reserveCount}
      />

      {pasteError !== null && <ActivateError error={pasteError} />}
    </>
  );
}

/**
 * FD-611 — "Credential lifetime" as a compact dropdown.
 *
 * A native Fluent `Select`, not the `Dropdown` listbox: this repo has no reliable
 * jsdom idiom for opening a Dropdown's portal (MoveDevicesDialog.test records why),
 * and this value reaches a credential Rubix cannot revoke, so it must stay testable
 * end to end. The default is the SERVER's (FD-442); "Custom…" keeps the per-wave
 * override to any whole number in 1–90, so nothing the free-form field allowed is lost.
 */
function CredentialLifetimeField({
  expiryDays,
  onExpiryDays,
  serverDefault,
  custom,
  onCustom,
  daysValid,
  disabled,
}: {
  expiryDays: string;
  onExpiryDays: (v: string) => void;
  serverDefault: number;
  custom: boolean;
  onCustom: (v: boolean) => void;
  daysValid: boolean;
  disabled: boolean;
}) {
  const presets = Array.from(new Set<number>([...LIFETIME_PRESETS, serverDefault])).sort((a, b) => a - b);
  const isPreset = presets.some((d) => String(d) === expiryDays);
  const selectValue = custom || !isPreset ? 'custom' : expiryDays;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <Label htmlFor="expiry-days" className="min-w-[130px]">
          Credential lifetime
        </Label>
        <Select
          id="expiry-days"
          value={selectValue}
          disabled={disabled}
          onChange={(_, d) => {
            if (d.value === 'custom') {
              onCustom(true);
              return;
            }
            onCustom(false);
            onExpiryDays(d.value);
          }}
          style={{ minWidth: 200 }}
        >
          {presets.map((d) => (
            <option key={d} value={String(d)}>
              {d} {d === 1 ? 'day' : 'days'}
              {d === serverDefault ? ' (default)' : ''}
            </option>
          ))}
          <option value="custom">Custom…</option>
        </Select>
      </div>
      {selectValue === 'custom' && (
        <div className="flex flex-wrap items-center gap-3">
          <Label htmlFor="expiry-days-custom" className="min-w-[130px]">
            Custom lifetime (days)
          </Label>
          <Input
            id="expiry-days-custom"
            type="number"
            min={1}
            max={90}
            value={expiryDays}
            onChange={(_, d) => onExpiryDays(d.value)}
            disabled={disabled}
            style={{ maxWidth: 120 }}
          />
        </div>
      )}
      {!daysValid && (
        <span className="text-xs" style={{ color: STATUS.errorText }}>
          Must be a whole number between 1 and 90.
        </span>
      )}
    </div>
  );
}

// ——— stage 2: the code, the account, the clock ————————————————————————

function SignInStage({
  phase,
  onCopy,
  onCancel,
}: {
  phase: { kind: 'awaiting-signin'; session: MintSession; secondsRemaining: number } | { kind: 'activating'; session: MintSession };
  onCopy: (code: string) => void;
  onCancel: () => void;
}) {
  const session = phase.session;
  const minting = phase.kind === 'activating';

  return (
    <>
      <p className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
        Open the sign-in page, enter this code, and authenticate as the account below. Keep this
        dialog open — AirBridge completes the activation as soon as you finish.
      </p>

      <div
        className="flex flex-col gap-3 p-4"
        style={{ border: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.rowFill }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span
            className="font-mono text-2xl font-semibold"
            style={{ color: TEXT.primary, letterSpacing: '0.18em' }}
            // A screen-reader user has to TYPE this. "K7QW2X9AB" read as a word
            // is useless; spaced characters are dictation you can follow.
            aria-label={`Code ${session.userCode.split('').join(' ')}`}
          >
            {session.userCode}
          </span>
          <div className="flex items-center gap-2">
            <Button
              appearance="secondary"
              icon={<Copy size={16} strokeWidth={1.5} />}
              onClick={() => onCopy(session.userCode)}
            >
              Copy code
            </Button>
            {/* A real anchor, not window.open — middle-click, copy-link and
                popup blockers all behave, and it needs no JS to work. */}
            <Button
              as="a"
              appearance="primary"
              href={session.verificationUrl}
              target="_blank"
              rel="noopener noreferrer"
              icon={<ExternalLink size={16} strokeWidth={1.5} />}
            >
              Open sign-in page
            </Button>
          </div>
        </div>

        <dl className="flex flex-col gap-1 font-display text-[13px]">
          <Row label="Sign in as">
            {session.serviceAccountUpn ? (
              // FD-440 (`FD-ADR-042` §5) — the RESOLVED account for THIS wave's
              // destination tenant, computed server-side as
              // `Tenants.BprtServiceAccountUpn` → `BPRT_SERVICE_ACCOUNT_UPN`. It is
              // rendered exactly as received and never reconstructed here: a console
              // that guessed would name one account while the Manager resolved
              // another, and a deployment with two destinations is precisely where
              // the two answers diverge.
              <span className="font-mono text-xs">{session.serviceAccountUpn}</span>
            ) : (
              // Nothing resolved — no per-tenant account AND no deployment default.
              // The server refuses to invent one and so does this: a wrong account
              // here is AADSTS240005.
              <span style={{ color: STATUS.warning }}>
                no account configured for this destination tenant — set it on Tenants ›
                Service accounts
              </span>
            )}
          </Row>
          <Row label="Destination tenant">
            <span className="font-mono text-xs">{session.tenantId}</span>
          </Row>
        </dl>
      </div>

      {minting ? (
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>Minting the wave credential</MessageBarTitle>
            Your sign-in landed. This step talks to the destination tenant and can take up to a
            minute — leave the dialog open.
          </MessageBarBody>
        </MessageBar>
      ) : (
        <div className="flex items-center gap-3">
          <Spinner size="tiny" />
          <span className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
            Waiting for the sign-in — code expires in{' '}
            <time className="font-mono" dateTime={session.expiresUtc}>
              {formatRemaining(phase.secondsRemaining)}
            </time>
          </span>
        </div>
      )}

      <p className="font-display text-xs" style={{ color: TEXT.secondary }}>
        Canceling sends nothing to the server. A code has no revoke — it expires on its own, and
        nothing has been minted yet.{' '}
        <button
          type="button"
          onClick={onCancel}
          className="appearance-none border-0 bg-transparent p-0 text-inherit outline-none underline focus-visible:outline focus-visible:outline-2"
          style={{ color: TEXT.secondary, cursor: 'pointer' }}
        >
          Cancel now
        </button>
      </p>
    </>
  );
}

// ——— stage 2 (browser): the tab, the account, the way back ——————————————

function BrowserSignInStage({
  phase,
  helperDetected,
  onSubmit,
}: {
  phase: Extract<BrowserSignInPhase, { kind: 'awaiting-return' } | { kind: 'activating' }>;
  /** A helper ping succeeded on this page at some point (FD-607). */
  helperDetected: boolean;
  onSubmit: (returnUrl: string) => void;
}) {
  const [pasted, setPasted] = useState('');
  const session = phase.session;
  const minting = phase.kind === 'activating';
  const helperWaiting = phase.kind === 'awaiting-return' && phase.helperRegistered;

  return (
    <>
      <p className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
        A Microsoft sign-in opened in a new tab. Sign in there as the account below. Keep this page
        open — reloading it loses the sign-in’s one-time key, and you would need to start again.
      </p>

      <div
        className="flex flex-col gap-3 p-4"
        style={{ border: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.rowFill }}
      >
        <dl className="flex flex-col gap-1 font-display text-[13px]">
          <Row label="Sign in as">
            {session.serviceAccountUpn ? (
              <span className="text-xs" style={IDENTIFIER}>
                {session.serviceAccountUpn}
              </span>
            ) : (
              <span style={{ color: STATUS.warningText }}>
                no account configured for this destination tenant — set it on Tenants › Service
                accounts
              </span>
            )}
          </Row>
          <Row label="Destination tenant">
            <span className="text-xs" style={IDENTIFIER}>
              {session.tenantId}
            </span>
          </Row>
        </dl>
        {!minting && phase.kind === 'awaiting-return' && (
          <div>
            {/* A real anchor, always: window.open after an await can be popup-
                blocked, and with `noopener` the console cannot tell. */}
            <Button
              as="a"
              appearance="secondary"
              href={phase.authorizeUrl}
              target="_blank"
              rel="noopener noreferrer"
              icon={<ExternalLink size={16} strokeWidth={1.5} />}
            >
              Open the Microsoft sign-in again
            </Button>
          </div>
        )}
      </div>

      {minting ? (
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>Minting the wave credential</MessageBarTitle>
            Your sign-in came back. This step talks to the destination tenant and can take up to a
            minute — leave the dialog open.
          </MessageBarBody>
        </MessageBar>
      ) : (
        <>
          {helperWaiting && (
            <div className="flex items-center gap-3">
              <Spinner size="tiny" />
              <span className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
                Waiting for the sign-in — the AirBridge sign-in helper will bring it back here.
              </span>
            </div>
          )}

          {/* FD-607: the helper answered on this page but did not take the
              registration. That is a relay that went stale under an open tab
              (the extension was updated, re-enabled or reloaded) and it will
              not bring the sign-in back, so say so rather than wait silently.
              A browser with no helper at all gets no alarm, only the paste
              instructions below. */}
          {!helperWaiting && helperDetected && (
            <MessageBar intent="warning" data-testid="helper-unresponsive">
              <MessageBarBody>
                The sign-in helper didn't respond in this tab. Reload this tab and start again, or
                paste the address below.
              </MessageBarBody>
            </MessageBar>
          )}

          <div className="flex flex-col gap-1">
            <Label htmlFor="browser-return-url">
              {helperWaiting ? 'Or paste the address the tab landed on' : 'Paste the address the tab landed on'}
            </Label>
            {!helperWaiting && (
              <span className="font-display text-xs" style={{ color: TEXT.secondary }}>
                The tab will end on a page that can't load (localhost). That's expected — copy its
                address and paste it here.
              </span>
            )}
            <Textarea
              id="browser-return-url"
              value={pasted}
              onChange={(_, d) => setPasted(d.value)}
              placeholder="http://localhost:…/?code=…&state=…"
              resize="vertical"
              rows={2}
            />
            <span className="font-display text-xs" style={{ color: TEXT.secondary }}>
              Sent once. It is redeemed on the server, which mints the credential; the token never
              reaches this browser.
            </span>
            <div>
              <Button
                appearance="primary"
                disabled={pasted.trim().length === 0}
                onClick={() => onSubmit(pasted)}
              >
                Activate with this address
              </Button>
            </div>
          </div>

          {phase.kind === 'awaiting-return' && phase.pasteError != null && (
            <ActivateError error={phase.pasteError} />
          )}
        </>
      )}
    </>
  );
}

// ——— stage 3: activated, plus the mint's tenant verdict ————————————————

/**
 * FD-341b (FD-332) — which tenant issued the credential ATC will check.
 *
 * Three states, and conflating any two of them is the failure mode. Every title
 * therefore opens with "Wave activated", because that is true in all three and
 * is the half an operator is most likely to lose: a mismatch is a warning about
 * a *completed* activation, not a failed one. RECORDED, NEVER ASSERTED — the
 * server does not block on this, so neither does the copy.
 *
 * The verdict is stated in WORDS. The tenant id below it is supporting detail;
 * nobody is asked to compare two GUIDs to learn the answer.
 */
function ActivatedStage({ verdict }: { verdict: AssertionIdentity }) {
  const matched = verdict.matchesDestination;

  return (
    <>
      {matched === true ? (
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>
              Wave activated — the destination tenant issued the credential
            </MessageBarTitle>
            The mint’s tenant assertion matches this campaign’s destination. Nothing to do.
          </MessageBarBody>
        </MessageBar>
      ) : matched === false ? (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>
              Wave activated — but a different tenant issued the credential
            </MessageBarTitle>
            {/* Both halves, in this order. Leading with the warning invites the
                reader to conclude activation failed and go hunting for a retry —
                which would mint a second credential. */}
            <strong>
              The wave is active and the credential is stored — nothing failed and there is nothing
              to undo.
            </strong>{' '}
            The tenant that issued it is not this campaign’s destination tenant. AirBridge records
            that and does not block on it, but the package build checks the same assertion — so look
            into this before building the wave’s provisioning package.
          </MessageBarBody>
        </MessageBar>
      ) : (
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>
              Wave activated — the issuing tenant was {verdict.tid ? 'not checked' : 'not recorded'}
            </MessageBarTitle>
            {/* Deliberately neither green nor amber. An older mint path produces
                no assertion, and that is ordinary — not a fault to chase. */}
            {verdict.tid
              ? 'The tenant was recorded but not compared against this campaign’s destination.'
              : 'The mint returned no readable tenant assertion, so there was nothing to compare. This is not a problem — an older mint path does not produce one.'}
          </MessageBarBody>
        </MessageBar>
      )}

      <div
        className="flex flex-col gap-3 p-4"
        style={{ border: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.rowFill }}
      >
        <dl className="flex flex-col gap-1 font-display text-[13px]">
          <Row label="Issuing tenant">
            {verdict.tid ? (
              <span className="font-mono text-xs">{verdict.tid}</span>
            ) : (
              <span style={{ color: TEXT.secondary }}>
                not recorded
                {verdict.reason ? (
                  <>
                    {' — '}
                    <span className="font-mono text-xs">{verdict.reason}</span>
                  </>
                ) : null}
              </span>
            )}
          </Row>
          {verdict.iat !== null && verdict.iat !== undefined && (
            <Row label="Issued">
              <time className="font-mono text-xs" dateTime={isoFromUnixSeconds(verdict.iat)}>
                {isoFromUnixSeconds(verdict.iat)}
              </time>
            </Row>
          )}
        </dl>
      </div>
    </>
  );
}

/** `iat` is unix seconds. Rendered as UTC — the console never localises a claim. */
function isoFromUnixSeconds(seconds: number): string {
  const d = new Date(seconds * 1_000);
  return Number.isNaN(d.getTime()) ? String(seconds) : d.toISOString();
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-2">
      <dt style={{ color: TEXT.secondary }}>{label}</dt>
      <dd style={{ color: TEXT.primary }}>{children}</dd>
    </div>
  );
}

function formatRemaining(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ——— errors ————————————————————————————————————————————————————————————

export function ActivateError({ error }: { error: unknown }) {
  const code = waveErrorCode(error);

  if (code === 'LICENSE_POOL_EXHAUSTED') {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>No migration license available</MessageBarTitle>
          {/* Named precisely because it is the ALWAYS-ON licensing block — the
              one every deployment has, whatever it configures. So this means
              capacity, not an outage. And on the guided path it means a
              completed sign-in was spent for nothing, which the operator has to
              be told or they will wonder what they left behind.

              This comment previously read "the ONLY licensing outcome that
              blocks", which FD-435 makes false: LICENSE_CREDITS_EXHAUSTED also
              refuses, on a deployment that has switched credit enforcement on
              (FD-ADR-041 decision 7, default off). Corrected here rather than
              left to age — the FD-270 lesson is that a superseded rule survives
              in the code statements nobody greps for. Since FD-ADR-041 this
              fires on `poolAvailable < N`, one slot per assigned device, rather
              than `< 1`. */}
          The license pool is at capacity, so <strong>nothing was minted — your sign-in was
          used but produced no credential</strong>. Free a slot by completing another wave, or
          contact Rubix, then sign in again.
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'LICENSE_CREDITS_EXHAUSTED') {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>Not enough migration credits for this wave</MessageBarTitle>
          {/* FD-435 (FD-ADR-041 decision 7). Deliberately NOT sharing a branch
              with LICENSE_POOL_EXHAUSTED: the pool is a platform capacity limit
              every deployment has, this is a commercial setting most deployments
              never switch on — and the remedies are different, so collapsing
              them would send an operator to the wrong one.

              The copy has three jobs: say nothing was minted, say why this
              deployment refused at all (because the default is to proceed), and
              name the two things that actually change the outcome. */}
          This deployment <strong>enforces</strong> the 1:1 credit bucket, which is off by
          default — so a wave that reserves more credits than the account has is refused
          instead of proceeding with a recorded shortfall. The check runs{' '}
          <strong>before the mint, so nothing was minted</strong>
          {/* The reorder in FD-ADR-032 §4 means the human has already signed in
              by the time the licence check runs, exactly as for the pool block. */}
          , but a guided sign-in was still spent.
          <br />
          Two things change this: reserve fewer — remove devices from this campaign, or move
          some into a separate one — or obtain more credits from Rubix. Then activate again.{' '}
          {errorText(error)}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'BPRT_MINT_ROLE_MISSING') {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>That account cannot mint</MessageBarTitle>
          The sign-in succeeded but the destination tenant refused the mint. The minting account
          needs <strong>Password Administrator</strong> (or Cloud Device / Intune Administrator)
          and must not be scoped to an administrative unit.
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'DEVICE_CODE_CLIENT_REJECTED') {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>The destination tenant does not know the sign-in app</MessageBarTitle>
          {/* Tenant variance, not a fault in the campaign — which the copy has
              to say, or an operator starts looking at the wrong thing. */}
          This is tenant configuration, not a problem with the campaign. Set
          <code> DRS_DEVICE_CODE_CLIENT_ID</code> to a public client that exists in that tenant,
          or paste a token instead. {errorText(error)}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'DEVICE_CODE_BLOCKED') {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>Conditional Access blocks the guided sign-in</MessageBarTitle>
          Exclude the minting account from the policy — which is already required for the
          MFA exclusion — or exclude the Device Registration Service resource. You can also
          paste a token instead.
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'AUTHORIZATION_DECLINED') {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>The sign-in was declined</MessageBarTitle>
          Nothing was minted. Start over if that was not intended.
        </MessageBarBody>
      </MessageBar>
    );
  }

  // ── FD-592: the browser sign-in (FD-591) ──────────────────────────────────

  if (code === 'STATE_MISMATCH') {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>That address is from a different sign-in</MessageBarTitle>
          It does not match the sign-in this page started — a stale tab, a second attempt, or an
          address someone else supplied. Nothing was minted. Paste the address from the tab this page
          opened, or start the sign-in again.
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'RETURN_URL_INVALID' || code === 'STATE_REQUIRED' || code === 'REDIRECT_URI_MISMATCH') {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>That is not the address the sign-in returned to</MessageBarTitle>
          Copy the whole address from the sign-in tab — it starts with <code>http://localhost</code>{' '}
          and carries a code. Nothing was minted. {errorText(error)}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (
    code === 'CODE_VERIFIER_INVALID' ||
    code === 'CODE_CHALLENGE_INVALID' ||
    code === 'REDIRECT_URI_INVALID' ||
    code === 'INVALID_MODE' ||
    code === 'CREDENTIAL_SOURCE_AMBIGUOUS'
  ) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>The console sent a sign-in the Manager could not accept</MessageBarTitle>
          This is a fault in the console or a version mismatch with the Manager, not in your sign-in.
          Nothing was minted. Start over; if it repeats, reload the page before starting and report
          it. {errorText(error)}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'AUTHORIZATION_REFUSED') {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>Microsoft Entra refused the sign-in</MessageBarTitle>
          The refusal happened before any credential existed, so nothing was minted.{' '}
          {errorText(error)}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'AUTHORIZATION_CODE_INVALID') {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>That sign-in can no longer be redeemed</MessageBarTitle>
          The code expired or was already used. Nothing was minted. Start over and sign in again.
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'AUTHORIZATION_CODE_REJECTED') {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>The destination tenant refused this sign-in, and will again</MessageBarTitle>
          {/* Not a stale code — a policy. Signing in again is refused the same way,
              so the copy points at the cause and the other paths, not at a retry. */}
          Nothing was minted. This is a tenant policy rather than a stale code, so signing in again
          the same way will be refused too. Check the destination tenant’s sign-in log for the
          reason, adjust the policy for the minting account, or use a different sign-in path.{' '}
          {errorText(error)}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'AUTHORIZATION_CODE_REDEEM_FAILED') {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>The sign-in could not be redeemed</MessageBarTitle>
          The Manager could not exchange the sign-in with Microsoft Entra, and nothing was minted.
          Start over to sign in again. {errorText(error)}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'DEVICE_CODE_EXPIRED') {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>That code is no longer usable</MessageBarTitle>
          It expired or was already redeemed. Nothing was minted — start over for a new code.
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code.startsWith('DRS_TOKEN_')) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>That token cannot mint this wave</MessageBarTitle>
          {/* Checked BEFORE any mint is attempted (FD-ADR-032 §5), so nothing
              has happened in the tenant. */}
          {errorText(error)}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'BPRT_STORE_FAILED') {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>A credential exists that AirBridge cannot revoke</MessageBarTitle>
          {/* The one path that leaks a live credential. It needs a human, not a
              retry button — retrying mints a SECOND orphan. */}
          The mint succeeded but storing it failed, so a <code>package_</code> user now exists in
          the destination tenant with no record here to revoke it.{' '}
          <strong>Tell your identity team before retrying.</strong> {errorText(error)}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'BPRT_STATE_STAMP_FAILED') {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>Activated, but the record did not update</MessageBarTitle>
          The credential was minted and stored; stamping the migration row failed. Retry to
          reconcile. {errorText(error)}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'BPRT_MINT_FAILED') {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>The credential mint failed</MessageBarTitle>
          The destination tenant rejected the mint. The usual causes are a Conditional Access
          policy that still applies to the minting account, or an account without the required
          role. {errorText(error)}
        </MessageBarBody>
      </MessageBar>
    );
  }

  if (code === 'DESTINATION_TENANT_UNRESOLVED') {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          <MessageBarTitle>This campaign has no destination tenant</MessageBarTitle>
          There is nothing to sign in to. Check the campaign’s destination — for a Hybrid
          campaign with no override, that is the deployment’s own tenant. {errorText(error)}
        </MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <MessageBar intent="error">
      <MessageBarBody>
        <MessageBarTitle>Could not activate the wave</MessageBarTitle>
        {errorText(error)}
      </MessageBarBody>
    </MessageBar>
  );
}
